import { EnvironmentProviders, inject } from '@angular/core';
import { TrackWorkflowService, provideWorkflowTracking } from '@anduin/shared';
import {
  PAYMENTS_STEP_TO_EVENT_TYPE,
  PAYMENTS_STEP_TO_WORKFLOW,
  PaymentsWorkflowStep,
  PaymentsWorkflowStepMetaDataMap,
} from './payments-workflow.models';

/**
 * Strongly-typed `TrackWorkflowService` view for the payments module. `trackWorkflow`
 * narrows `metadata` to the shape declared on `PaymentsWorkflowStepMetaDataMap` for
 * whatever step the caller passes. Inherits `track` / `identify` / `reset` /
 * `attachToIframe` from `TrackService`, so a single injected reference covers both
 * general events and workflow tracking.
 */
export type PaymentsTrackService = TrackWorkflowService<PaymentsWorkflowStep, PaymentsWorkflowStepMetaDataMap>;

/**
 * Inject the workflow tracker narrowed to the payments taxonomy. Use this in place of
 * `inject(TrackService)` anywhere in the payments module that needs `trackWorkflow` —
 * the base `TrackService` doesn't expose `trackWorkflow` at all, so the unnarrowed
 * surface is a compile error (TS2339).
 */
export function injectPaymentsTracking(): PaymentsTrackService {
  return inject(TrackWorkflowService) as PaymentsTrackService;
}

/**
 * Register the payments module's workflow taxonomy. Call once at app providers (or at the
 * payments route-tree boundary) so `trackWorkflow(<payments step>)` resolves at runtime.
 */
export function providePaymentsWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<PaymentsWorkflowStep, PaymentsWorkflowStepMetaDataMap>({
    stepToWorkflow: PAYMENTS_STEP_TO_WORKFLOW,
    stepToEventType: PAYMENTS_STEP_TO_EVENT_TYPE,
  });
}
